import { gql } from '@apollo/client';

const DEADLINE_MS = 120_000;
const POLL_MS = 2_000;
const SETTLED = ['processed', 'error', 'failed'];

export const ARTY_FLYOUT_IMAGE_QUERY = gql`
  query ArtyFlyoutImage($id: ID!) {
    image(id: $id) {
      id
      title
      status
      optimizedUrl
      thumbnailUrl
    }
  }
`;

export type FlyoutImage = { imageId: string; url: string; title?: string };

function wait(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true }
    );
  });
}

function toFlyoutImage(image: any, fallbackId: string): FlyoutImage | null {
  const url = image?.optimizedUrl;
  if (!url || !/^https?:\/\//.test(url)) {
    return null;
  }

  return { imageId: image?.id != null ? String(image.id) : fallbackId, url, title: image?.title ?? undefined };
}

/** A network blip is worth another poll; a GraphQL error (e.g. someone else's piece) will not change. */
function isTransient(error: any) {
  return Boolean(error?.networkError) && !error?.graphQLErrors?.length;
}

async function fetchImage(apolloClient: any, imageId: string, signal: AbortSignal) {
  const { data } = await apolloClient.query({
    query: ARTY_FLYOUT_IMAGE_QUERY,
    variables: { id: imageId },
    fetchPolicy: 'network-only',
    context: { fetchOptions: { signal } },
  });

  return data?.image ?? null;
}

/** The sparklink's piece, polled while it is still processing, for at most two minutes. Null when it never resolves. */
export async function resolveFlyoutImage(
  apolloClient: any,
  imageId: string | undefined,
  signal: AbortSignal
): Promise<FlyoutImage | null> {
  if (!imageId) {
    return null;
  }

  const deadline = Date.now() + DEADLINE_MS;
  let image: any = null;

  while (Date.now() < deadline && !signal.aborted) {
    try {
      image = await fetchImage(apolloClient, imageId, signal);
    } catch (error) {
      if (signal.aborted || !isTransient(error)) {
        return null;
      }
      await wait(POLL_MS, signal);
      continue;
    }

    if (!image) {
      return null;
    }

    if (SETTLED.includes(image.status)) {
      return toFlyoutImage(image, imageId);
    }

    await wait(POLL_MS, signal);
  }

  return toFlyoutImage(image, imageId);
}
